import { z } from 'zod';
import {
  Id,
  CanonicalId,
  Digest,
  Target,
  Scope,
  Property,
  ResolveInput,
  FreezeInput,
  parse,
} from './contracts.mjs';
import { PatchValue, PreparePatchInput, ApplyPatchInput, UndoPatchInput } from './patch-values.mjs';
import { SlotPresentation } from './slot-presentation.mjs';
import { PublicInspectManifest } from './manifest-view.mjs';
import { PublicNativeMapping } from './native-map.mjs';
import { requireCondition } from './errors.mjs';
import { shape, id as sesId } from '../../session-plane/contracts/browser-values.mjs';

// Wire envelopes compose existing C16 values; none is a grant or a private repository row.
export const InspectBinding = z.strictObject({
  sessionId: Id,
  artifactId: CanonicalId,
  revisionId: CanonicalId,
  buildId: Id,
});
export const InspectActionTicket = z.discriminatedUnion('action', [
  InspectBinding.extend({ action: z.literal('apply'), operationId: Id, operationDigest: Digest }),
  InspectBinding.extend({
    action: z.literal('undo'),
    operationId: Id,
    expectedRevisionId: CanonicalId,
    idempotencyKey: Id,
  }),
]);
const inputs = Object.freeze({
  bootstrap: z.strictObject({}),
  acquire: z.strictObject({}),
  release: z.custom((lease) => {
    try {
      shape(lease, ['leaseId'], [], 'inspectLease');
      sesId(lease.leaseId, 'leaseId');
      return true;
    } catch {
      return false;
    }
  }),
  resolve: ResolveInput,
  freeze: FreezeInput,
  prepare: PreparePatchInput,
  apply: ApplyPatchInput,
  undo: UndoPatchInput,
  intent: InspectActionTicket,
  receipt: InspectActionTicket,
});
export const NATIVE_INSPECT_OPERATIONS = Object.freeze(Object.keys(inputs));
export function parseInspectRequest(operation, raw) {
  requireCondition(Object.hasOwn(inputs, operation), 'inspect_operation_unavailable');
  const request = parse(z.strictObject({ binding: InspectBinding, input: inputs[operation] }), raw);
  requireCondition(Object.hasOwn(raw, 'input'), 'invalid_inspect_input');
  if (
    ['resolve', 'freeze', 'prepare'].includes(operation) &&
    request.input.artifactId !== undefined
  )
    assertBinding(request.binding, request.input, ['artifactId', 'revisionId', 'buildId']);
  if (operation === 'intent' || operation === 'receipt')
    assertBinding(request.binding, request.input, Object.keys(request.binding));
  return request;
}
function assertBinding(binding, input, fields) {
  requireCondition(
    fields.every((field) => input[field] === binding[field]),
    'inspect_binding_mismatch',
  );
}
const TargetDetails = z.strictObject({
  targetId: Id,
  source: z.discriminatedUnion('status', [
    z.strictObject({
      status: z.literal('available'),
      value: z.strictObject({
        kind: z.literal('artifact-node'),
        nodeId: CanonicalId,
      }),
    }),
    z.strictObject({ status: z.literal('redacted') }),
  ]),
  slots: z
    .array(
      z.strictObject({
        slotId: Id,
        property: Property,
        value: PatchValue,
        scope: Scope,
        editable: z.boolean(),
        clearable: z.boolean(),
        presentation: SlotPresentation.optional(),
      }),
    )
    .max(32),
  bindingRefs: z.array(CanonicalId).max(32),
});
export const NativeInspectBootstrap = z.strictObject({
  manifest: PublicInspectManifest,
  mapping: PublicNativeMapping,
});
export const InspectResolveResult = z.strictObject({
  current: z.strictObject({ revisionId: CanonicalId, buildId: Id }),
  manifestDigest: Digest,
  targets: z.array(Target).min(1).max(16),
  details: z.array(TargetDetails).min(1).max(16),
});
export const InspectPreparedResult = z.strictObject({ operationId: Id, operationDigest: Digest });
export const InspectIntentResult = z.strictObject({ hostIntent: Id });
export const InspectFrozenResult = z.strictObject({ selectionSetId: Id });
export const InspectCommittedView = z.strictObject({
  operationId: Id,
  artifactId: CanonicalId,
  revisionId: CanonicalId,
  operationReceiptRef: CanonicalId,
  versionLabel: z.string().max(200).nullable(),
  undoIdempotencyKey: Id.optional(),
});
export const InspectCommitReceipt = z.discriminatedUnion('status', [
  z.strictObject({ status: z.literal('committed'), value: InspectCommittedView }),
  z.strictObject({ status: z.literal('absent') }),
  z.strictObject({ status: z.literal('unresolved') }),
]);
export function parseInspectReceipt(raw, ticket) {
  const receipt = parse(InspectCommitReceipt, raw);
  if (receipt.status === 'committed') {
    requireCondition(
      receipt.value.operationId === ticket.operationId &&
        receipt.value.artifactId === ticket.artifactId,
      'receipt_identity_mismatch',
    );
    requireCondition(
      ticket.action !== 'undo' || receipt.value.undoIdempotencyKey === ticket.idempotencyKey,
      'receipt_identity_mismatch',
    );
  }
  return receipt;
}
